import React, { useRef } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { bluePlanetTheme } from 'ui/theme';
import { fontSizeSmall, fontSizeTiny } from 'ui/theme/themeBluePlanet';
import { halfSpacing, quarterSpacing } from 'ui/theme/themeConstants';
import { companyUrls } from 'urls';
import { timeSince } from 'util/dateUtils';
import { CompanyLatestPost } from 'types/company';
import usePostImpression from 'hooks/usePostImpression';

const PostLink = styled(Link)`
  display: block;
  padding: ${quarterSpacing} ${halfSpacing};
  border-left: 2px solid ${bluePlanetTheme.bluePlanetPalette.grey.medium};
  color: ${bluePlanetTheme.bluePlanetPalette.indigo.main};

  &:hover {
    border-left-color: ${bluePlanetTheme.bluePlanetPalette.blue.main};
  }
`;

const PostedAt = styled.div`
  font-size: ${fontSizeTiny};
  color: ${bluePlanetTheme.bluePlanetPalette.grey.main};
`;

const Excerpt = styled.p`
  font-size: ${fontSizeSmall};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

interface Props {
  companyId: number;
  companySlug: string;
  latestPost: CompanyLatestPost;
  /** Open the post in a new tab, e.g. when the card is shown inside a flow the user shouldn't leave */
  openInNewTab?: boolean;
  className?: string;
}

export default function LatestPostExcerpt({ companyId, companySlug, latestPost, openInNewTab, className }: Props) {
  const impressionRef = useRef<HTMLAnchorElement>(null);
  usePostImpression(latestPost.contentId, impressionRef, 'CompanyCard');

  return (
    <PostLink
      ref={impressionRef}
      className={className}
      to={companyUrls.update.view(companySlug, latestPost.contentId)}
      onClick={e => e.stopPropagation()}
      {...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      <PostedAt>Latest post · {timeSince(latestPost.createdAt)}</PostedAt>
      <Excerpt>{latestPost.excerpt}</Excerpt>
    </PostLink>
  );
}
